import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, finalize, map, Observable, of, switchMap } from 'rxjs';
import { environment } from '../../environments/environment';
import { IBucket, isSystemBucket } from './bucket.service';

/**
 * The Creator's library in the portal (al-filemanager `/creator/…`,
 * features/docs/technical/creator-items.md). The same routes canvas uses;
 * forms follow the frozen AlphaCourse contract (`ItemSummary`,
 * `ListItemsResponse`, `CreateAcItemRequest`). Errors come as the envelope
 * `{error, message}`: every call suppresses the global error toast and the
 * page says what went wrong itself.
 */

export type CreatorItemKind = 'exercise_set' | 'moodle_course' | 'elearning_course' | 'microlearning';

export const AC_KINDS: readonly string[] = ['elearning_course', 'microlearning'];
/** course.format of each AlphaCourse kind (contract AC_KIND_FORMAT). */
export const AC_KIND_FORMAT: Readonly<Record<string, string>> = { elearning_course: 'course', microlearning: 'microlearning' };
/** Kinds the knowledge-base target can render (al-filemanager creator_kb_ops). */
export const KB_KINDS: readonly string[] = ['exercise_set', 'moodle_course'];
/** Library filter (Q92): all, exercises, every kind of course. */
export const KIND_FILTERS: Readonly<Record<'all' | 'exercises' | 'courses', readonly string[]>> = {
  all: [],
  exercises: ['exercise_set'],
  courses: ['moodle_course', 'elearning_course', 'microlearning'],
};
export type KindFilter = keyof typeof KIND_FILTERS;
/** Second filter under „Kurse": one kind of course, or all of them. */
export const COURSE_KIND_FILTERS: readonly string[] = ['all', 'moodle_course', 'elearning_course', 'microlearning'];

export interface PersonRef { id: string; name: string; }

export interface LockInfo { by: PersonRef; until: string; isRun: boolean; isSupport?: boolean; }

export interface LiveTarget { target: string; versionNo: number; status: string; outdated: boolean; }

export interface ItemSummary {
  id: string;
  kind: string;
  title: string;
  status: string;
  revision: number;
  updatedAt: string;
  updatedBy?: PersonRef;
  createdBy?: PersonRef;
  lock?: LockInfo;
  changedSinceVersion?: number;
  liveIn?: LiveTarget[];
  format?: string;
  kitId?: string;
  visibility?: string;
}

export interface ListItemsResponse {
  items: ItemSummary[];
  total: number;
  limit: number;
  offset: number;
  /** AT extension: this person may only read (support access without write rights). */
  readOnly?: boolean;
}

export interface TrashItem {
  id: string;
  kind: string;
  title: string;
  deletedAt: string;
  restorableUntil: string;
  deletedBy?: PersonRef;
}

export interface TenantKit { id: string; name: string; version: number; }

export interface CreateAcItem {
  kind: string;
  title: string;
  kitId: string;
  lang: string;
  voice: 'du' | 'sie';
}

/** An import into the Creator (al-filemanager `/creator/imports`, plan 4-01). */
export interface CreatorImport {
  importId: string;
  type: string;
  /** needs_confirmation: a large document waits for `confirmImport` (or `deleteImport`). */
  status: 'uploading' | 'queued' | 'running' | 'needs_confirmation' | 'done' | 'failed';
  fileName: string;
  bytes: number;
  partBytes: number;
  parts: number;
  receivedParts: number[];
  itemId?: string;
  kind?: string;
  title?: string;
  summary?: { format: string; lessons: number; blocks: number; needsReview: number; uploads: number; rejected: number; fonts: number };
  warnings?: { code: string; severity: string; message: string }[];
  rejected?: { fileName: string; code: string; message: string }[];
  errorCode?: string;
  limit?: { name: string; value: number; unit: string };
  code?: string;
}

/** One Figma connection (own or the organisation's) as settings › connections shows it — never the token. */
export interface CreatorConnection {
  status: 'connected' | 'unreadable' | string;
  connectedAt?: string;
  tokenLast4?: string;
  account?: { handle?: string; email?: string };
}

/** `GET /creator/connections` (ADR 0013): own, the organisation's, may the person set the organisation's. */
export interface CreatorConnections {
  figma: { personal: CreatorConnection | null; tenant: CreatorConnection | null; canManageTenant: boolean };
}

export type CreatorImportOptions =
  | { type: 'rise' }
  | { type: 'document'; kind: 'elearning_course' | 'microlearning'; lang?: string };

/** An error of a Creator route: the code of the envelope and its message. */
export interface CreatorFailure { status: number; code: string; message: string; }

export function creatorFailure(err: unknown): CreatorFailure {
  const e = err as HttpErrorResponse;
  const body = (e?.error ?? {}) as { error?: unknown; message?: unknown };
  return {
    status: typeof e?.status === 'number' ? e.status : 0,
    code: typeof body.error === 'string' ? body.error : 'internal',
    message: typeof body.message === 'string' ? body.message : '',
  };
}

@Injectable({ providedIn: 'root' })
export class CreatorLibraryService {
  private readonly base = `${environment.filesBackend}/creator`;
  private readonly quiet = new HttpHeaders({ 'X-Suppress-Error-Toast': '1' });

  constructor(private http: HttpClient) {}

  /** One page of the tenant's content, newest change first. */
  list(limit: number, offset: number, kinds: readonly string[] = [], query = ''): Observable<ListItemsResponse> {
    let params = new HttpParams().set('limit', limit).set('offset', offset);
    for (const kind of kinds) params = params.append('kind', kind);
    // title search on the server, so it covers every page
    if (query.trim()) params = params.set('q', query.trim());
    return this.http.get<ListItemsResponse>(`${this.base}/items`, { params, headers: this.quiet });
  }

  /**
   * Does this tenant have the library? Off (404, rollout allowlist) or no
   * Creator access (403) means the Creator stays the way it was — the canvas
   * start page in its frame. A network error is not "unavailable".
   */
  available(): Observable<'yes' | 'no' | 'error'> {
    return this.access().pipe(map((access) => access.answer));
  }

  /** `available()` plus whether this person may only read — the server decides
   *  (support access writes only where al-filemanager allows it). */
  access(): Observable<{ answer: 'yes' | 'no' | 'error'; readOnly: boolean }> {
    return this.list(1, 0).pipe(
      map((page) => ({ answer: 'yes' as const, readOnly: page.readOnly === true })),
      catchError((err: HttpErrorResponse) =>
        of({ answer: err.status === 404 || err.status === 403 ? ('no' as const) : ('error' as const), readOnly: true }),
      ),
    );
  }

  createAc(item: CreateAcItem): Observable<{ id: string; revision: number }> {
    return this.http.post<{ id: string; revision: number }>(
      `${this.base}/items`,
      { ...item, sourceKind: 'blank', format: AC_KIND_FORMAT[item.kind] },
      { headers: this.quiet },
    );
  }

  remove(id: string): Observable<void> {
    return this.http.delete<void>(`${this.base}/items/${id}`, { headers: this.quiet });
  }

  trash(): Observable<TrashItem[]> {
    return this.http.get<{ items: TrashItem[] }>(`${this.base}/items/trash`, { headers: this.quiet }).pipe(map((r) => r.items));
  }

  undelete(id: string): Observable<unknown> {
    return this.http.post(`${this.base}/items/${id}/undelete`, {}, { headers: this.quiet });
  }

  /**
   * New title, the way every write goes: take the lock for this one change,
   * `PATCH` with it, give it back. Someone else editing → `locked` (with
   * `by`). AlphaCourse courses get the title in the course too (server).
   */
  rename(id: string, title: string): Observable<{ title: string }> {
    const sessionId = `library-${crypto.randomUUID()}`;
    return this.http.post<{ lockId: string }>(`${this.base}/items/${id}/lock`, { sessionId }, { headers: this.quiet }).pipe(
      switchMap((lock) =>
        this.http
          .patch<{ title: string }>(`${this.base}/items/${id}`, { title }, { headers: this.quiet.set('X-AC-Lock-Id', lock.lockId) })
          .pipe(
            finalize(() => {
              this.http.delete(`${this.base}/items/${id}/lock`, { headers: this.quiet, params: { sessionId } }).subscribe({ error: () => undefined });
            }),
          ),
      ),
    );
  }

  /** "Für alle freigeben" (Q94): own private content for the whole organisation. */
  release(id: string): Observable<unknown> {
    return this.http.post(`${this.base}/items/${id}/release`, {}, { headers: this.quiet });
  }

  /** The tenant's own Brand Kits; the built-in ones live in the course editor. */
  kits(): Observable<TenantKit[]> {
    return this.http.get<{ items: TenantKit[] }>(`${this.base}/kits`, { headers: this.quiet }).pipe(
      map((r) => (r.items ?? []).map((k) => ({ id: k.id, name: k.name, version: k.version }))),
    );
  }

  /** Version of the saved working state (an existing one is reused). */
  saveVersion(id: string): Observable<number> {
    return this.http.post<{ versionNo: number }>(`${this.base}/items/${id}/versions`, {}, { headers: this.quiet })
      .pipe(map((r) => r.versionNo));
  }

  /** That version into a knowledge base; returns the job id. */
  publishToKb(id: string, bucketId: string, versionNo: number, includeSolutions: boolean): Observable<string> {
    return this.http.put<{ jobId: string }>(
      `${this.base}/items/${id}/kb`,
      { bucketId, versionNo, includeSolutions },
      { headers: this.quiet },
    ).pipe(map((r) => r.jobId));
  }

  /** The knowledge bases this person may put content into, without internal system folders. */
  buckets(): Observable<IBucket[]> {
    return this.http.get<{ data: IBucket[] }>(`${environment.filesBackend}/bucket`, { headers: this.quiet })
      .pipe(map((r) => (r.data ?? [])
        .filter((b) => !isSystemBucket(b))
        .map((b) => ({ id: b.id, name: b.name, description: b.description ?? '' }))));
  }

  /** Reserve an import of an Articulate Rise package; the answer says how to send it in parts. */
  /** A Rise package (`type: 'rise'`) or a document (`'document'`, with the
   *  format the person picked as `kind` and the language). */
  createImport(file: File, options: CreatorImportOptions = { type: 'rise' }): Observable<CreatorImport> {
    return this.http.post<CreatorImport>(
      `${this.base}/imports`, { ...options, fileName: file.name, bytes: file.size }, { headers: this.quiet },
    );
  }

  importPart(importId: string, n: number, part: Blob): Observable<{ receivedBytes: number }> {
    return this.http.put<{ receivedBytes: number }>(
      `${this.base}/imports/${importId}/parts/${n}`, part,
      { headers: this.quiet.set('Content-Type', 'application/octet-stream') },
    );
  }

  startImport(importId: string): Observable<{ jobId: string }> {
    return this.http.post<{ jobId: string }>(`${this.base}/imports/${importId}/start`, {}, { headers: this.quiet });
  }

  /** A large document asked first (`needs_confirmation`): it becomes a course after all. */
  confirmImport(importId: string): Observable<{ jobId: string }> {
    return this.http.post<{ jobId: string }>(`${this.base}/imports/${importId}/confirm`, {}, { headers: this.quiet });
  }

  getImport(importId: string): Observable<CreatorImport> {
    return this.http.get<CreatorImport>(`${this.base}/imports/${importId}`, { headers: this.quiet });
  }

  deleteImport(importId: string): Observable<unknown> {
    return this.http.delete(`${this.base}/imports/${importId}`, { headers: this.quiet });
  }

  /** Figma for the course editor: own and the organisation's connection (settings › connections). */
  connections(): Observable<CreatorConnections> {
    return this.http.get<CreatorConnections>(`${this.base}/connections`, { headers: this.quiet });
  }

  /** Figma checks the token before it is stored; `tenant` = the organisation's (admin only). */
  connectFigma(token: string, tenant = false): Observable<CreatorConnection> {
    return this.http.put<CreatorConnection>(
      `${this.base}/connections/figma${tenant ? '/tenant' : ''}`, { token }, { headers: this.quiet },
    );
  }

  disconnectFigma(tenant = false): Observable<unknown> {
    return this.http.delete(`${this.base}/connections/figma${tenant ? '/tenant' : ''}`, { headers: this.quiet });
  }

  jobState(jobId: string): Observable<string> {
    return this.http.get<{ state: string }>(`${this.base}/jobs/${jobId}`, { headers: this.quiet }).pipe(map((r) => r.state));
  }
}
